import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { token } from '../../tokens/tokens';
import { ChartFrame } from '../../molecules/ChartFrame/ChartFrame';
import {
  isEmptyTotal,
  type ChartBaseProps,
  type ChartRow,
} from '../../molecules/ChartFrame/chartProps';
import { ChartLegend } from '../../molecules/ChartLegend/ChartLegend';
import { ChartTooltip } from '../../molecules/ChartTooltip/ChartTooltip';
import {
  chartAnimation,
  seriesColor,
  type ChartSeriesToken,
} from '../../molecules/ChartFrame/chartTheme';
import { AnchoredTooltipSurface } from '../../molecules/ChartFrame/AnchoredTooltipSurface';
import { useAnchoredTooltip } from '../../molecules/ChartFrame/useAnchoredTooltip';
import styles from './DonutChart.module.css';

/**
 * A donut chart.
 *
 * **Figma designs none of this.** The `Charts` page specifies the cartesian
 * chrome only — axes, gridlines, legend — and there is no pie or donut anywhere
 * in the file, so every number below is derived: the hole size, the ring
 * thickness, the hairline between slices and the centre readout. The palette and
 * the legend are the file's; the shape is not. See plan.md §7 item 132.
 *
 * ```tsx
 * <DonutChart data={rows} nameKey="channel" valueKey="sessions" centerLabel="Sessions" />
 * ```
 */
export type DonutChartProps = Omit<ChartBaseProps, 'xAxisTitle' | 'yAxisTitle'> & {
  data: ChartRow[];
  /** Key holding each slice's name. */
  nameKey: string;
  /** Key holding each slice's value. */
  valueKey: string;
  /** Caption under the centre figure. */
  centerLabel?: string;
  /**
   * The centre figure. Left undefined, the slice values are summed and run
   * through `formatValue` — which is the number a donut is usually asked for.
   */
  centerValue?: string;
  /** Override the palette per slice, in order. */
  colors?: ChartSeriesToken[];
};

export function DonutChart({
  data,
  nameKey,
  valueKey,
  height,
  legend = 'right',
  tooltip = true,
  loading = false,
  empty,
  emptyLabel,
  formatValue,
  formatCompare,
  label,
  centerLabel,
  centerValue,
  colors,
  onViewDetails,
  viewDetailsLabel,
  className,
}: DonutChartProps) {
  const slices = data.map((row, i) => ({
    name: String(row[nameKey] ?? ''),
    value: Number(row[valueKey] ?? 0),
    color: seriesColor(i, colors?.[i]),
  }));

  const total = slices.reduce((sum, s) => sum + (Number.isFinite(s.value) ? s.value : 0), 0);

  const anchor = useAnchoredTooltip({
    count: slices.length,
    enabled: tooltip,
    onDrill: onViewDetails,
  });
  const anchoredSlice = anchor.point ? slices[anchor.point.index] : undefined;
  /* Shared with callers, so a `Card` can ask the same question — see `isEmptyTotal`. */
  const isEmptyDonut = isEmptyTotal(data, valueKey);

  const resolvedCentre = centerValue ?? (formatValue ? formatValue(total) : String(total));
  const showCentre = centerLabel !== undefined || centerValue !== undefined;

  return (
    <ChartFrame
      height={height}
      loading={loading}
      empty={empty ?? isEmptyDonut}
      emptyLabel={emptyLabel}
      label={label ?? `Donut chart: ${slices.map((s) => s.name).join(', ')}`}
      legend={
        legend === 'none' ? undefined : (
          <ChartLegend
            items={slices.map((s) => ({ label: s.name, color: s.color }))}
            orientation={legend === 'right' ? 'vertical' : 'horizontal'}
          />
        )
      }
      legendPlacement={legend === 'right' ? 'right' : 'bottom'}
      /*
       * Only beside a legend. A ring centred in a wide plot is what Figma's
       * `Chart` slot shows, and with the legend below — or absent — the surplus
       * width sits symmetrically either side and reads as nothing. Put the legend
       * to the right and that same surplus lands entirely between the ring and
       * the key, where it reads as a gap the `gap` rule cannot reach.
       */
      squarePlot={legend === 'right'}
      overlay={
        showCentre ? (
          <>
            {centerLabel !== undefined && <span className={styles.centerLabel}>{centerLabel}</span>}
            <span className={styles.centerValue}>{resolvedCentre}</span>
          </>
        ) : undefined
      }
      plotRef={anchor.plotRef}
      plotProps={anchor.plotProps}
      hover={
        anchoredSlice && (
          <AnchoredTooltipSurface anchor={anchor}>
            {/*
             * One entry, not a row of series: on a donut the slice *is* the
             * series, which is also why the heading is hidden — the name is
             * already the only row.
             */}
            <ChartTooltip
              active
              hideLabel
              payload={[
                {
                  name: anchoredSlice.name,
                  value: anchoredSlice.value,
                  color: anchoredSlice.color,
                  dataKey: valueKey,
                  payload: data[anchor.point?.index ?? 0],
                },
              ]}
              formatter={formatValue && ((v) => formatValue(Number(v)))}
              compare={
                formatCompare &&
                ((entry) =>
                  entry.payload ? formatCompare(entry.payload, String(entry.dataKey)) : undefined)
              }
              action={anchor.drill}
              actionLabel={viewDetailsLabel}
              locked={anchor.locked}
            />
          </AnchoredTooltipSurface>
        )
      }
      className={className}
    >
      <PieChart {...anchor.chartProps}>
        {tooltip && (
          /*
           * Kept mounted although a donut draws no cursor, so the chart still
           * reports an active sector to `chartProps` — see `LineChart` for the
           * arrangement. `content` renders nothing; the bubble is anchored to
           * the sector by `ChartFrame`'s `hover` slot.
           */
          <Tooltip {...anchor.cursorProps} isAnimationActive={false} content={() => null} />
        )}
        <Pie
          data={slices}
          dataKey="value"
          nameKey="name"
          /* Derived. A 60/85 ring leaves the hole big enough to carry a figure
             at `Display xs` without crowding the slices. */
          innerRadius="60%"
          outerRadius="85%"
          /* A hairline in the panel's own colour separates adjacent slices
             without introducing a border colour the design never specified. */
          stroke={token['surface-card']}
          strokeWidth={2}
          {...chartAnimation}
        >
          {slices.map((s) => (
            <Cell key={s.name} fill={s.color} />
          ))}
        </Pie>
      </PieChart>
    </ChartFrame>
  );
}
